import { createContext, useContext, useMemo, useState } from 'react';

/**
 * Shared filter state for the Carrier Engagement dashboard.
 *
 * The filter pills live in the TopBar, while the data table that consumes them
 * lives in the routed page — different branches of the tree. This provider
 * wraps the shell so both sides share one source of truth, mirroring
 * WorkflowBarProvider.
 *
 * Scope note: the model type filter is the only group wired to the backend
 * today. HCC is centralized here too so the table can map it in later; the
 * workflow chevrons and the Qs-to-SmartLight / New Comments buttons remain
 * self-contained in the header until their own tickets wire them up.
 */
// Selection state for the filter pills (model type, HCC, …). Changes whenever
// any pill is toggled.
const CarrierEngagementFilterContext = createContext(null);
// The derived backend query, split into its own context so the data table can
// subscribe to it alone — toggling a pill that doesn't feed the query (e.g. HCC
// today) won't churn the table. See useCarrierEngagementApiFilters below.
const CarrierEngagementApiFiltersContext = createContext(null);

// Sentinel "show everything" value — selecting it clears the model type
// contribution to the backend query.
const ALL = 'All';
const DEFAULT_MODEL_TYPE = ALL;
const DEFAULT_HCC = 'Exclude HCC';

function CarrierEngagementFilterProvider({ children }) {
  const [modelType, setModelType] = useState(DEFAULT_MODEL_TYPE);
  const [hcc, setHcc] = useState(DEFAULT_HCC);

  // The filter object sent to /api/v1/referrals/. Memoized so OverviewTable's
  // refetch effect only fires when a selection actually changes (not on every
  // render). This is the single home for the cross-group business rules.
  const apiFilters = useMemo(() => {
    const filters = {};
    if (modelType && modelType !== ALL) {
      filters.model_type = modelType;
    }
    // TODO: map `hcc` here once the backend supports it (add it to the
    // dependency array below when it does).
    return filters;
  }, [modelType]);

  // Pill selection state lives in its own value, deliberately excluding
  // apiFilters. Bundling them would give the table a new context reference on
  // every HCC toggle, re-rendering it (and, once HCC feeds apiFilters, firing a
  // redundant fetch alongside the intended one). Keeping the two contexts
  // separate lets each consumer subscribe to only what it reacts to.
  const selection = useMemo(
    () => ({ modelType, setModelType, hcc, setHcc }),
    [modelType, hcc]
  );

  return (
    <CarrierEngagementFilterContext.Provider value={selection}>
      <CarrierEngagementApiFiltersContext.Provider value={apiFilters}>
        {children}
      </CarrierEngagementApiFiltersContext.Provider>
    </CarrierEngagementFilterContext.Provider>
  );
}

function useCarrierEngagementFilters() {
  const ctx = useContext(CarrierEngagementFilterContext);
  if (!ctx) {
    throw new Error(
      'useCarrierEngagementFilters must be used within a CarrierEngagementFilterProvider'
    );
  }
  return ctx;
}

function useCarrierEngagementApiFilters() {
  const ctx = useContext(CarrierEngagementApiFiltersContext);
  if (ctx === null) {
    throw new Error(
      'useCarrierEngagementApiFilters must be used within a CarrierEngagementFilterProvider'
    );
  }
  return ctx;
}

export {
  CarrierEngagementFilterProvider,
  useCarrierEngagementFilters,
  useCarrierEngagementApiFilters,
  DEFAULT_MODEL_TYPE,
  DEFAULT_HCC,
};
